import { authService } from "@/services/AuthService";
import { User } from "oidc-client-ts";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { AuthContext } from "./UseAuth";

interface UserState {
  from?: string;
}

export interface AuthProviderProps {
  children: ReactNode;
  loadingElement?: ReactNode;
}

export const AuthProvider = ({
  children,
  loadingElement = null,
}: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshAuthState = useCallback(async () => {
    const authenticated = await authService.isAuthenticated();

    if (!authenticated) {
      console.log("User is not authenticated");
      setUser(null);
      setIsAuthenticated(false);
      return;
    }

    console.log("User is authenticated");
    const user = await authService.getUser();
    setUser(user);
    setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      console.log("Initializing authentication...");
      try {
        await refreshAuthState();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [refreshAuthState]);

  const login = useCallback(async (returnUrl: string) => {
    await authService.login(returnUrl);
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
  }, []);

  const completeLogin = useCallback(async (): Promise<string> => {
    const user = await authService.completeLogin();
    setUser(user);
    setIsAuthenticated(true);

    return (user.state as UserState)?.from || "/";
  }, []);

  if (isLoading) {
    return loadingElement;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        logout,
        login,
        completeLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
